import screenshot from 'src/assets/img/screenshot.webp';

/**
 * Region map for the interactive feature showcase.
 *
 * Every rectangle is measured in pixels on the original 1525 x 886 screenshot and
 * converted to normalized (0..1) coordinates by `rect()`. To nudge a highlight,
 * edit the numbers passed to `rect(x, y, width, height)` below.
 *
 * The stage keeps the image's exact aspect ratio, so normalized coordinates map
 * 1:1 onto the rendered image at any size (no object-fit offsets to compensate).
 */

export const IMAGE = {
  src: screenshot,
  width: 1525,
  height: 886,
} as const

export type Rect = { x: number; y: number; w: number; h: number }

const rect = (x: number, y: number, w: number, h: number): Rect => {return {
  x: x / IMAGE.width,
  y: y / IMAGE.height,
  w: w / IMAGE.width,
  h: h / IMAGE.height,
}}

/** The application display inside the laptop bezel. */
export const SCREEN: Rect = rect(163, 38, 1199, 700)

/**
 * How far a lifted copy travels. `dx` / `dy` are fractions of the stage WIDTH
 * (so the motion scales with the laptop); `scale` is the enlargement.
 */
export type Lift = { dx: number; dy: number; scale: number }

export type FeaturePart = {
  rect: Rect
  lift: Lift
  /** Tabs only: the cyan pill that moves between two tab labels. */
  indicator?: { from: Rect; to: Rect }
}

export type FeatureIcon = 'folder' | 'tabs' | 'split' | 'archive' | 'user' | 'grid'

export type Feature = {
  id: string
  label: string
  caption: string
  icon: FeatureIcon
  parts: FeaturePart[]
  /** false = keep the original region dimmed too (used when parts move apart). */
  hole?: boolean
  /** Normalized x where the connector leaves the lifted copy (default: centre). */
  anchorX?: number
}

export const FEATURES: Feature[] = [
  {
    id: 'workspaces',
    label: 'Workspaces',
    caption: 'Keep every team and project in its own workspace.',
    icon: 'folder',
    parts: [
      {
        // Far-left workspace navigation panel
        rect: rect(165, 72, 171, 666),
        lift: { dx: -0.014, dy: -0.012, scale: 1.035 },
      },
    ],
  },
  {
    id: 'tabs',
    label: 'Tabs',
    caption: 'Switch between tools without losing your context.',
    icon: 'tabs',
    parts: [
      {
        // Horizontal tab strip: Dashboard, Editor, Video, New Whiteboard, Brainfuel, Messenger
        rect: rect(386, 38, 508, 30),
        lift: { dx: 0, dy: -0.02, scale: 1.4 },
        indicator: {
          from: rect(530, 41, 66, 25), // Video
          to: rect(806, 41, 80, 25), // Messenger
        },
      },
    ],
  },
  {
    id: 'split',
    label: 'Split screen',
    caption: 'Keep your call and your work side by side.',
    icon: 'split',
    hole: false,
    anchorX: 607 / IMAGE.width,
    parts: [
      {
        // Video call pane
        rect: rect(389, 72, 217, 666),
        lift: { dx: -0.014, dy: -0.006, scale: 1.02 },
      },
      {
        // Messenger pane
        rect: rect(608, 72, 569, 666),
        lift: { dx: 0.014, dy: -0.006, scale: 1.02 },
      },
    ],
  },
  {
    id: 'archived',
    label: 'Archived groups',
    caption: 'Find past groups when you need them.',
    icon: 'archive',
    parts: [
      {
        // "Archived" entry just below the search field
        rect: rect(167, 111, 170, 24),
        lift: { dx: 0.2, dy: -0.004, scale: 2.1 },
      },
    ],
  },
  {
    id: 'chats',
    label: '1:1 conversations',
    caption: 'Start a private conversation with a teammate.',
    icon: 'user',
    parts: [
      {
        // Far-right "Chats" contact list
        rect: rect(1181, 40, 181, 698),
        lift: { dx: 0.016, dy: -0.012, scale: 1.035 },
      },
    ],
  },
  {
    id: 'tools',
    label: 'Collaboration tools',
    caption: 'Access your collaboration tools in one place.',
    icon: 'grid',
    parts: [
      {
        // Narrow vertical tool rail between the workspace sidebar and the video panel
        rect: rect(337, 72, 52, 388),
        lift: { dx: 0.05, dy: -0.004, scale: 1.3 },
      },
    ],
  },
]

export const TAB_FEATURE_ID = 'tabs'

/** Tour timing (ms). */
export const TIMING = {
  introDelay: 1700,
  hold: 4400,
  gap: 800,
  tabSwitch: 1300,
}

/** Smallest rectangle containing every part of a feature. */
export function focusRect(feature: Feature): Rect {
  const xs = feature.parts.map((p) => {return p.rect.x})
  const ys = feature.parts.map((p) => {return p.rect.y})
  const xe = feature.parts.map((p) => {return p.rect.x + p.rect.w})
  const ye = feature.parts.map((p) => {return p.rect.y + p.rect.h})
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  return { x, y, w: Math.max(...xe) - x, h: Math.max(...ye) - y }
}
